// Thin typed fetch wrapper. Session is an httpOnly cookie; errors carry server code/detail.
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public detail?: unknown) { super(message); }
}

async function handle(res: Response) {
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') ?? '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const b = typeof body === 'object' && body ? body : { error: 'HTTP', message: String(body) };
    throw new ApiError(res.status, b.error ?? 'HTTP', b.message ?? res.statusText, b.detail);
  }
  return body;
}

export const api = {
  get: <T = any>(url: string) => fetch(url, { credentials: 'include' }).then(handle) as Promise<T>,
  post: <T = any>(url: string, body?: unknown) => fetch(url, { method: 'POST', credentials: 'include', headers: body === undefined ? {} : { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) }).then(handle) as Promise<T>,
  patch: <T = any>(url: string, body: unknown) => fetch(url, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(handle) as Promise<T>,
  put: <T = any>(url: string, body: unknown) => fetch(url, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(handle) as Promise<T>,
  del: <T = any>(url: string) => fetch(url, { method: 'DELETE', credentials: 'include' }).then(handle) as Promise<T>,
  upload: <T = any>(url: string, file: File, fields: Record<string, string> = {}) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    fd.append('file', file);
    return fetch(url, { method: 'POST', credentials: 'include', body: fd }).then(handle) as Promise<T>;
  },
};

export const fmt = {
  money: (v: unknown, digits = 0) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: digits, minimumFractionDigits: digits })),
  num: (v: unknown, digits = 0) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits })),
  pct: (v: unknown, digits = 1) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(digits)}%`),
  date: (v: unknown) => (!v ? '—' : String(v).slice(0, 10)),
  dt: (v: unknown) => (!v ? '—' : new Date(String(v)).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })),
  ago: (v: unknown) => {
    if (!v) return '—';
    const s = (Date.now() - new Date(String(v)).getTime()) / 1000;
    if (s < 60) return 'just now'; if (s < 3600) return `${Math.round(s / 60)}m ago`; if (s < 86400) return `${Math.round(s / 3600)}h ago`; return `${Math.round(s / 86400)}d ago`;
  },
  label: (v: unknown) => (v === null || v === undefined ? '—' : String(v).replace(/_/g, ' ')),
};

export const today = () => new Date().toISOString().slice(0, 10);
